import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v3';
import { type Account, accountApi } from '@/api/commercial/accounts';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/components/ui/form-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { LocalAccount } from '@/context/create-account-flow';
import { useFormError } from '@/hooks/use-form-error';
import { useToast } from '@/hooks/use-toast';
import { isValidCNPJ, isValidCPF, maskCpfCnpj } from '@/lib/formatters';

const schema = z
  .object({
    documentType: z.enum(['cpf', 'cnpj'], {
      required_error: 'Selecione o tipo de documento',
    }),
    document: z.string().min(1, 'Documento obrigatório'),
    name: z.string().min(1, 'Nome obrigatório'),
    social_name: z.string().optional(),
    fantasy_name: z.string().optional(),
    state_registration: z.string().optional(),
    municipal_registration: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const cleanDoc = data.document.replace(/\D/g, '');

    if (data.documentType === 'cpf') {
      if (cleanDoc.length !== 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CPF deve ter 11 dígitos',
          path: ['document'],
        });
      } else if (!isValidCPF(cleanDoc)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CPF inválido',
          path: ['document'],
        });
      }
    } else {
      if (cleanDoc.length !== 14) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CNPJ deve ter 14 dígitos',
          path: ['document'],
        });
      } else if (!isValidCNPJ(cleanDoc)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CNPJ inválido',
          path: ['document'],
        });
      }
    }
  });

type AccountCreateFormData = z.infer<typeof schema>;

interface AccountCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (account: Account) => void;
}

export function AccountCreateModal({ open, onClose, onSuccess }: AccountCreateModalProps) {
  const { formError, showFormError, clearFormError } = useFormError(open);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AccountCreateFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      documentType: 'cpf', // Changed from 'cnpj' to avoid accidentally creating PF accounts as PJ
      document: '',
      name: '',
      social_name: '',
      fantasy_name: '',
      state_registration: '',
      municipal_registration: '',
    },
  });

  const documentType = watch('documentType');
  const _documentValue = watch('document');

  const createAccountMutation = useMutation({
    mutationFn: async (data: AccountCreateFormData) => {
      const accountData: Partial<LocalAccount> = {
        document: {
          type: data.documentType,
          value: data.document,
        },
      };

      if (data.documentType === 'cpf') {
        accountData.name = data.name;
        accountData.accountType = 'PF';
      } else {
        accountData.name = data.name;
        accountData.social_name = data.social_name || data.name;
        accountData.fantasy_name = data.fantasy_name || data.social_name || data.name;
        accountData.state_registration = data.state_registration;
        accountData.municipal_registration = data.municipal_registration;
      }

      const response = await accountApi.create(accountData as Omit<Account, 'id' | 'created_at'>);
      return accountApi.findOne(response.id);
    },
    onSuccess: (account) => {
      toast({
        variant: 'success',
        title: 'Conta criada',
        description: 'A conta foi criada com sucesso!',
      });

      queryClient.invalidateQueries({
        queryKey: ['accounts'],
      });

      onSuccess(account);

      reset();
      onClose();
    },
    onError: (error) => {
      console.error('Error creating account:', error);
      showFormError(error);
    },
  });

  const onSubmit = (data: AccountCreateFormData) => {
    createAccountMutation.mutate(data);
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = maskCpfCnpj(e.target.value);
    setValue('document', masked);
  };

  const isCnpj = documentType === 'cnpj';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova Conta</DialogTitle>
          <DialogDescription>
            Crie uma conta rápida para adicionar à proposta. Preencha os dados básicos abaixo.
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmitCapture={clearFormError}
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-4"
        >
          {/* Tipo de Documento */}
          <div className="space-y-1.5">
            <Label htmlFor="documentType">Tipo de Documento *</Label>
            <Select
              value={documentType}
              onValueChange={(value: 'cpf' | 'cnpj') => {
                setValue('documentType', value);
                setValue('document', '');
              }}
            >
              <SelectTrigger id="documentType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cpf">CPF (Pessoa Física)</SelectItem>
                <SelectItem value="cnpj">CNPJ (Pessoa Jurídica)</SelectItem>
              </SelectContent>
            </Select>
            {errors.documentType && (
              <p className="text-xs text-red-500">{errors.documentType.message}</p>
            )}
          </div>

          {/* Documento */}
          <div className="space-y-1.5">
            <Label htmlFor="document">{isCnpj ? 'CNPJ' : 'CPF'} *</Label>
            <Input
              id="document"
              {...register('document')}
              placeholder={isCnpj ? '00.000.000/0000-00' : '000.000.000-00'}
              onChange={handleDocumentChange}
              maxLength={isCnpj ? 18 : 14}
              aria-invalid={!!errors.document}
            />
            {errors.document && <p className="text-xs text-red-500">{errors.document.message}</p>}
          </div>

          {/* Nome / Razão Social */}
          <div className="space-y-1.5">
            <Label htmlFor="name">{isCnpj ? 'Razão Social' : 'Nome Completo'} *</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder={isCnpj ? 'Razão Social da empresa' : 'Nome completo'}
              aria-invalid={!!errors.name}
            />
            {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
          </div>

          {/* Campos específicos para CNPJ */}
          {isCnpj && (
            <>
              {/* Nome Fantasia */}
              <div className="space-y-1.5">
                <Label htmlFor="fantasy_name">Nome Fantasia</Label>
                <Input
                  id="fantasy_name"
                  {...register('fantasy_name')}
                  placeholder="Nome fantasia (opcional)"
                />
              </div>

              {/* Inscrição Estadual */}
              <div className="space-y-1.5">
                <Label htmlFor="state_registration">Inscrição Estadual</Label>
                <Input
                  id="state_registration"
                  {...register('state_registration')}
                  placeholder="Inscrição Estadual (opcional)"
                />
              </div>

              {/* Inscrição Municipal */}
              <div className="space-y-1.5">
                <Label htmlFor="municipal_registration">Inscrição Municipal</Label>
                <Input
                  id="municipal_registration"
                  {...register('municipal_registration')}
                  placeholder="Inscrição Municipal (opcional)"
                />
              </div>
            </>
          )}

          <FormError error={formError} onDismiss={clearFormError} />
          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-black text-white hover:bg-gray-800"
              disabled={isSubmitting || createAccountMutation.isPending}
            >
              {(isSubmitting || createAccountMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Criar Conta
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
